import Honeytoken from "../shield/Honeytoken";
import { Box, Languages, Menu, Moon, Sun, UserRound, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useI18n } from "../i18n";
import { useSession } from "../lib/session";
import { applyTheme, useTheme } from "../lib/theme";

const NAV = [
  { to: "/features", key: "nav.features" },
  { to: "/donate", key: "nav.donate" },
  { to: "/download", key: "nav.download" },
  { to: "/docs", key: "nav.docs" },
  { to: "/faq", key: "nav.faq" },
  { to: "/support", key: "nav.support" },
];
// The plugin catalog is a separate site on its own host (same server, see docs/*/plugins-catalog.md).
const PLUGINS_URL = "https://plugins.formacad.ru";

export function Brand({ sub }: { sub?: string }) {
  const { t } = useI18n();
  return (
    <Link to="/" className="brand" aria-label={t("nav.homeAria")}>
      <span className="brand-mark"><Box size={21} strokeWidth={1.7} aria-hidden /></span>
      forma
      {sub && <><span className="brand-divider" /><span className="brand-sub">{sub}</span></>}
    </Link>
  );
}

function Topbar() {
  const { t, locale, setLocale } = useI18n();
  const theme = useTheme();
  const session = useSession();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  const signedIn = session.data?.authenticated;
  return (
    <>
      <header className="topbar">
        <Brand sub="CAD" />
        <nav className="topnav" aria-label={t("nav.mainAria")}>
          {NAV.map((n) => <NavLink key={n.to} to={n.to}>{t(n.key)}</NavLink>)}
          <a href={PLUGINS_URL}>{t("nav.plugins")}</a>
        </nav>
        <div className="topbar-actions">
          <button className="icon-button" onClick={() => setLocale(locale === "ru" ? "en" : "ru")} aria-label={t("nav.switchLanguage")} title={t("nav.switchLanguage")}>
            <Languages size={16} aria-hidden /> {locale === "ru" ? "EN" : "RU"}
          </button>
          <button className="icon-button" onClick={() => applyTheme(theme === "dark" ? "light" : "dark")} aria-label={theme === "dark" ? t("nav.themeLight") : t("nav.themeDark")} title={theme === "dark" ? t("nav.themeLight") : t("nav.themeDark")}>
            {theme === "dark" ? <Sun size={16} aria-hidden /> : <Moon size={16} aria-hidden />}
          </button>
          {signedIn ? (
            <Link className="button" to="/account"><UserRound size={15} aria-hidden /><span className="label">{t("nav.account")}</span></Link>
          ) : (
            <Link className="button primary" to="/login"><span>{t("nav.signIn")}</span></Link>
          )}
          <button className="icon-button menu-button" aria-expanded={open} aria-controls="mobile-nav" aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")} onClick={() => setOpen(!open)}>
            {open ? <X size={18} aria-hidden /> : <Menu size={18} aria-hidden />}
          </button>
        </div>
      </header>
      {open && (
        <nav id="mobile-nav" className="mobile-nav" aria-label={t("nav.mainAria")}>
          {NAV.map((n) => <NavLink key={n.to} to={n.to}>{t(n.key)}</NavLink>)}
          <a href={PLUGINS_URL}>{t("nav.plugins")}</a>
        </nav>
      )}
    </>
  );
}

function Footer() {
  const { t } = useI18n();
  return (
    <footer className="footer">
      <div className="container footer-grid">
        <div className="stack" style={{ gap: 8 }}>
          <Brand />
          <p>{t("footer.tagline")}</p>
          <p className="small">{t("footer.localFirst")}</p>
        </div>
        <div>
          <h4>{t("footer.product")}</h4>
          <Link to="/features">{t("nav.features")}</Link>
          <Link to="/donate">{t("nav.donate")}</Link>
          <Link to="/download">{t("nav.download")}</Link>
          <Link to="/requirements">{t("nav.requirements")}</Link>
          <Link to="/changelog">{t("changelog.title")}</Link>
          <a href={PLUGINS_URL}>{t("nav.pluginsCatalog")}</a>
        </div>
        <div>
          <h4>{t("footer.help")}</h4>
          <Link to="/docs">{t("nav.docs")}</Link>
          <Link to="/faq">{t("nav.faq")}</Link>
          <Link to="/support">{t("nav.support")}</Link>
          <a href="https://github.com/lovlyDev/FormaCAD" rel="noopener noreferrer">GitHub</a>
        </div>
        <div>
          <h4>{t("footer.legal")}</h4>
          <Link to="/legal/terms">{t("legal.terms")}</Link>
          <Link to="/legal/privacy">{t("legal.privacy")}</Link>
          <Link to="/legal/offer">{t("legal.offer")}</Link>
          <span className="small">{t("footer.drafts")}</span>
        </div>
      </div>
    </footer>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  return (
    <div className="shell">
      <a className="skip-link" href="#main">{t("nav.skip")}</a>
      <Topbar />
      <Honeytoken />
      <main id="main" tabIndex={-1}>{children}</main>
      <Footer />
    </div>
  );
}
